import { Link } from 'expo-router';
import { useIsFocused } from '@react-navigation/native';
import { ChevronDown } from 'lucide-react-native';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  type ViewToken,
  View,
} from 'react-native';

import { ErrorView } from '@/src/components/ErrorView';
import { LoadingView } from '@/src/components/LoadingView';
import { Screen } from '@/src/components/Screen';
import { useAuth } from '@/src/features/auth/use-auth';
import { usePublishedChangeLogEntries } from '@/src/features/changelog/use-changelog';
import { CommentComposeModal } from '@/src/features/comments/CommentComposeModal';
import { InviteEducationModal } from '@/src/features/invites/InviteEducationModal';
import { FeedModeToggle } from '@/src/features/posts/FeedModeToggle';
import { PostCard } from '@/src/features/posts/PostCard';
import { PostPhotoViewerModal } from '@/src/features/posts/PostPhotoViewerModal';
import { PostVideoViewerModal } from '@/src/features/posts/PostVideoViewerModal';
import { useFeedEvents } from '@/src/features/posts/use-feed-events';
import { usePublicPosts } from '@/src/features/posts/use-public-posts';
import type { FeedMode, PublicPost } from '@/src/features/posts/post-service';
import { PwaInstallCard } from '@/src/features/pwa/PwaInstallCard';
import { useProfile } from '@/src/features/profile/use-profile';
import { useAppStore } from '@/src/state/app-store';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';
import { formatDate } from '@/src/utils/dates';
import { useRefreshableListInteractions } from '@/src/utils/refreshable-list-interactions';
import { useRegisterTabScrollHandler } from '@/src/utils/tab-scroll';

function EmptyFeed() {
  return (
    <View style={styles.emptyState}>
      <Text style={styles.emptyTitle}>No posts yet</Text>
      <Text style={styles.emptyText}>
        Tap the plus button to share the first public post on Frienzzi.
      </Text>
    </View>
  );
}

function ChangeLogTimeline() {
  const changelog = usePublishedChangeLogEntries(3);
  const [expanded, setExpanded] = useState(false);
  const hasEntries = changelog.entries.length > 0;

  return (
    <View style={styles.changelogCard}>
      <View style={styles.changelogHeader}>
        <Pressable
          accessibilityLabel={
            expanded
              ? 'Collapse transparency updates'
              : 'Expand transparency updates'
          }
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          onPress={() => setExpanded((current) => !current)}
          style={({ pressed }) => [
            styles.changelogToggle,
            pressed ? styles.changelogTogglePressed : null,
          ]}
        >
          <View style={styles.changelogTitleGroup}>
            <Text style={styles.changelogEyebrow}>Transparency</Text>
            <Text numberOfLines={1} style={styles.changelogTitle}>
              Latest Frienzzi changes
            </Text>
          </View>
          <View
            style={[
              styles.changelogToggleIcon,
              expanded ? styles.changelogToggleIconOpen : null,
            ]}
          >
            <ChevronDown color={colors.mutedText} size={20} strokeWidth={2.6} />
          </View>
        </Pressable>
      </View>
      {expanded ? (
        <View style={styles.changelogList}>
          {hasEntries ? (
            changelog.entries.map((entry, index) => (
              <View key={entry.id} style={styles.changelogRow}>
                <View style={styles.changelogMarkerColumn}>
                  <View style={styles.changelogDot} />
                  {index < changelog.entries.length - 1 ? (
                    <View style={styles.changelogLine} />
                  ) : null}
                </View>
                <View style={styles.changelogEntry}>
                  <Text style={styles.changelogDate}>
                    {formatDate(entry.publishedAt)}
                  </Text>
                  <Text style={styles.changelogEntryTitle}>{entry.title}</Text>
                  <Text style={styles.changelogSummary}>{entry.summary}</Text>
                </View>
              </View>
            ))
          ) : (
            <View style={styles.changelogStatusRow}>
              {changelog.loading ? (
                <ActivityIndicator color={colors.primary} size="small" />
              ) : null}
              <Text style={styles.changelogStatusText}>
                {changelog.loading
                  ? 'Loading updates...'
                  : changelog.error
                    ? 'Updates could not load right now.'
                    : 'No published updates yet.'}
              </Text>
            </View>
          )}
          <Link asChild href="/changelog">
            <Pressable
              accessibilityRole="button"
              style={({ pressed }) => [
                styles.changelogButton,
                pressed ? styles.changelogButtonPressed : null,
              ]}
            >
              <Text style={styles.changelogButtonText}>View all updates</Text>
            </Pressable>
          </Link>
        </View>
      ) : null}
    </View>
  );
}

function HomeHeader({
  feedMode,
  onFeedModeChange,
}: {
  feedMode: FeedMode;
  onFeedModeChange: (mode: FeedMode) => void;
}) {
  return (
    <View style={styles.homeHeader}>
      <PwaInstallCard compact />
      <ChangeLogTimeline />
      <FeedModeToggle mode={feedMode} onChange={onFeedModeChange} />
    </View>
  );
}

export default function HomeScreen() {
  const { user } = useAuth();
  const isFocused = useIsFocused();
  const feedMode = useAppStore((state) => state.feedModePreferences.home);
  const setFeedModePreference = useAppStore(
    (state) => state.setFeedModePreference,
  );
  const feed = usePublicPosts(user?.uid, undefined, feedMode, isFocused);
  const profileQuery = useProfile(user?.uid);
  const listRef = useRef<FlatList<PublicPost>>(null);
  const [commentPost, setCommentPost] = useState<PublicPost | null>(null);
  const [videoViewerPostId, setVideoViewerPostId] = useState<string | null>(
    null,
  );
  const [videoViewerInitialPost, setVideoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerPostId, setPhotoViewerPostId] = useState<string | null>(
    null,
  );
  const [photoViewerInitialPost, setPhotoViewerInitialPost] =
    useState<PublicPost | null>(null);
  const [photoViewerInitialIndex, setPhotoViewerInitialIndex] = useState(0);
  const [inviteEducationHidden, setInviteEducationHidden] = useState(false);
  const feedEvents = useFeedEvents('home', isFocused ? user?.uid : undefined);
  const listInteractions = useRefreshableListInteractions({
    listRef,
    onRefresh: feed.refresh,
    refreshing: feed.refreshing,
  });

  useRegisterTabScrollHandler('home', listInteractions.scrollToTop);

  useEffect(() => {
    setInviteEducationHidden(false);
  }, [user?.uid]);

  const handleViewableItemsChanged = useRef(
    ({ viewableItems }: { viewableItems: ViewToken<PublicPost>[] }) => {
      viewableItems.forEach((viewableItem) => {
        if (viewableItem.isViewable && viewableItem.item?.id) {
          feedEvents.recordImpression(viewableItem.item.id);
        }
      });
    },
  ).current;
  const handleCommentPress = useCallback(
    (post: PublicPost) => {
      feedEvents.recordEvent({ postId: post.id, type: 'comment_open' }, true);
      setCommentPost(post);
    },
    [feedEvents],
  );
  const handleFeedFeedback = useCallback(
    (post: PublicPost, type: 'hide' | 'less_like_this') => {
      feedEvents.recordEvent({ postId: post.id, type }, true);
      feed.refresh();
    },
    [feed, feedEvents],
  );

  if (feed.loading && feed.posts.length === 0) {
    return <LoadingView message="Loading the public feed..." />;
  }

  if (feed.error && feed.posts.length === 0) {
    return (
      <Screen>
        <ErrorView message={feed.error.message} />
      </Screen>
    );
  }

  return (
    <>
      <Screen scroll={false} contentStyle={styles.feedScreenContent}>
        <FlatList
          ref={listRef}
          contentContainerStyle={styles.feedContent}
          data={feed.posts}
          keyExtractor={(post) => post.id}
          ListFooterComponent={
            feed.loadingMore ? (
              <View style={styles.feedFooter}>
                <ActivityIndicator color={colors.primary} />
              </View>
            ) : null
          }
          ListHeaderComponent={
            <HomeHeader
              feedMode={feedMode}
              onFeedModeChange={(nextMode) => {
                setFeedModePreference('home', nextMode);
              }}
            />
          }
          ListEmptyComponent={<EmptyFeed />}
          onEndReached={feed.loadMore}
          onEndReachedThreshold={0.4}
          onRefresh={feed.refresh}
          onScroll={listInteractions.onScroll}
          onViewableItemsChanged={handleViewableItemsChanged}
          renderItem={({ item }) => (
            <PostCard
              post={item}
              onCommentPress={handleCommentPress}
              onDetailOpen={(post) => {
                feedEvents.recordEvent(
                  { postId: post.id, type: 'detail_open' },
                  true,
                );
              }}
              onFeedFeedback={
                feedMode === 'ranked' ? handleFeedFeedback : undefined
              }
              onPostEngagement={(post) => {
                feedEvents.recordEvent(
                  { postId: post.id, type: 'post_engagement' },
                  true,
                );
              }}
              onPhotoPress={(post, photoIndex) => {
                feedEvents.recordEvent(
                  { postId: post.id, type: 'detail_open' },
                  true,
                );
                setPhotoViewerInitialPost(post);
                setPhotoViewerPostId(post.id);
                setPhotoViewerInitialIndex(photoIndex);
              }}
              onVideoPress={(post) => {
                feedEvents.recordEvent(
                  { postId: post.id, type: 'detail_open' },
                  true,
                );
                setVideoViewerInitialPost(post);
                setVideoViewerPostId(post.id);
              }}
            />
          )}
          refreshing={feed.refreshing}
          scrollEventThrottle={16}
          showsVerticalScrollIndicator={false}
          viewabilityConfig={{ itemVisiblePercentThreshold: 55 }}
          {...listInteractions.webPullToRefreshHandlers}
        />
      </Screen>
      <CommentComposeModal
        post={commentPost}
        visible={Boolean(commentPost)}
        onClose={() => setCommentPost(null)}
      />
      <PostVideoViewerModal
        key={videoViewerPostId ?? 'post-video-viewer'}
        hasMorePosts={feed.hasMore}
        initialPost={videoViewerInitialPost}
        initialPostId={videoViewerPostId}
        loadingMorePosts={feed.loadingMore}
        posts={feed.posts}
        visible={Boolean(videoViewerPostId)}
        onClose={() => {
          setVideoViewerPostId(null);
          setVideoViewerInitialPost(null);
        }}
        onCommentPress={handleCommentPress}
        onLoadMorePosts={feed.loadMore}
        onPostEngagement={(post) => {
          feedEvents.recordEvent(
            { postId: post.id, type: 'post_engagement' },
            true,
          );
        }}
      />
      <PostPhotoViewerModal
        key={
          photoViewerPostId
            ? `${photoViewerPostId}:${photoViewerInitialIndex}`
            : 'post-photo-viewer'
        }
        hasMorePosts={feed.hasMore}
        initialPhotoIndex={photoViewerInitialIndex}
        initialPost={photoViewerInitialPost}
        initialPostId={photoViewerPostId}
        loadingMorePosts={feed.loadingMore}
        posts={feed.posts}
        visible={Boolean(photoViewerPostId)}
        onClose={() => {
          setPhotoViewerPostId(null);
          setPhotoViewerInitialPost(null);
          setPhotoViewerInitialIndex(0);
        }}
        onCommentPress={handleCommentPress}
        onLoadMorePosts={feed.loadMore}
        onPostEngagement={(post) => {
          feedEvents.recordEvent(
            { postId: post.id, type: 'post_engagement' },
            true,
          );
        }}
      />
      <InviteEducationModal
        uid={user?.uid}
        visible={Boolean(
          user?.uid &&
          profileQuery.data &&
          !profileQuery.data.inviteEducationDismissedAt &&
          !inviteEducationHidden,
        )}
        onDismissed={() => setInviteEducationHidden(true)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  feedScreenContent: {
    alignSelf: 'stretch',
    maxWidth: '100%',
    paddingBottom: 0,
    paddingHorizontal: 0,
    paddingTop: 0,
    width: '100%',
  },
  feedContent: {
    alignSelf: 'center',
    gap: spacing.lg,
    maxWidth: 640,
    paddingBottom: 0,
    paddingHorizontal: spacing.sm,
    paddingTop: spacing.md,
    width: '100%',
  },
  feedFooter: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  emptyState: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.sm,
    padding: spacing.xl,
  },
  emptyTitle: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  emptyText: {
    color: colors.mutedText,
    fontSize: 15,
    lineHeight: 22,
  },
  homeHeader: {
    gap: spacing.lg,
  },
  changelogCard: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.md,
    padding: spacing.lg,
  },
  changelogHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
  },
  changelogToggle: {
    alignItems: 'center',
    borderRadius: 8,
    flex: 1,
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
    minHeight: 44,
  },
  changelogTogglePressed: {
    opacity: 0.78,
  },
  changelogTitleGroup: {
    flex: 1,
    gap: spacing.xs,
  },
  changelogEyebrow: {
    color: colors.secondary,
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  changelogTitle: {
    color: colors.text,
    fontSize: 18,
    fontWeight: '900',
  },
  changelogToggleIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ rotate: '0deg' }],
  },
  changelogToggleIconOpen: {
    transform: [{ rotate: '180deg' }],
  },
  changelogList: {
    gap: 0,
  },
  changelogButton: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    marginLeft: 22,
    marginTop: spacing.xs,
    minHeight: 36,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  changelogButtonPressed: {
    backgroundColor: colors.surfacePressed,
  },
  changelogButtonText: {
    color: colors.primary,
    fontSize: 14,
    fontWeight: '900',
  },
  changelogRow: {
    flexDirection: 'row',
  },
  changelogMarkerColumn: {
    alignItems: 'center',
    width: 22,
  },
  changelogDot: {
    backgroundColor: colors.primary,
    borderRadius: 5,
    height: 10,
    marginTop: spacing.sm,
    width: 10,
  },
  changelogLine: {
    backgroundColor: colors.border,
    flex: 1,
    width: 2,
  },
  changelogEntry: {
    flex: 1,
    gap: spacing.xs,
    paddingBottom: spacing.md,
  },
  changelogDate: {
    color: colors.mutedText,
    fontSize: 12,
    fontWeight: '700',
  },
  changelogEntryTitle: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
  },
  changelogSummary: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  changelogStatusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    paddingBottom: spacing.md,
  },
  changelogStatusText: {
    color: colors.mutedText,
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
});
